import { Box, Text } from '@chakra-ui/react'

import { useLogin, useVerification } from '@/api/auth'
import { obscureEmail } from '@/utils/obscuration'
import { VerificationCodeInput } from '@/components/input/verification-code-input'
import { ButtonWithTimer } from '@/components/button/button-with-timer'
import IconAbloWhite from '@/components/icons/IconAbloWhite'
import { H3 } from '@/components/ui/Headings'
import { useEffect, useState } from 'react'

type VerificationFormProps = {
  email: string
  code: string
}

export function VerificationForm({
  email,
  code: initialCode
}: VerificationFormProps) {
  const RESEND_INTERVAL_SECONDS = 60

  const { login } = useLogin()
  const { verify } = useVerification()

  const [code, setCode] = useState('')
  const [error, setError] = useState<string>()

  useEffect(() => {
    setCode(initialCode)
  }, [initialCode])

  const handleSubmit = () => {
    verify(
      { email, code },
      {
        onError: () => setError('Invalid verification code, please try again'),
        onSuccess: () => setError(undefined)
      }
    )
  }

  const handleResendCode = () => {
    login(email)
    setError(undefined)
  }

  return (
    <Box
      w="100%"
      maxW="520px"
      px="24px"
      display="flex"
      flexDirection="column"
      alignItems="center"
      position="relative"
      zIndex={1}
    >
      <Box className="animate-fade-in-up">
        <Box className="animate-text-glow-sweep">
          <IconAbloWhite width={180} height={128} />
        </Box>
      </Box>

      <Box className="animate-fade-in-up animate-delay-100">
        <Box
          h="1px"
          w="80px"
          mx="auto"
          mb="12px"
          mt="12px"
          className="animate-shimmer"
          bg="rgba(0, 112, 243, 0.2)"
        />
        <Text
          color="white"
          textAlign="center"
          textStyle="label"
          mb="48px"
        >
          Design Intelligence Platform
        </Text>
      </Box>

      <Box
        w="100%"
        bg="rgba(18, 18, 26, 0.5)"
        backdropFilter="blur(16px)"
        borderRadius="20px"
        border="1px solid"
        borderColor="border.subtle"
        p={{ base: '28px', md: '40px' }}
        display="flex"
        flexDirection="column"
        alignItems="center"
        className="animate-fade-in-up animate-delay-300"
      >
        <H3
          color="text.primary"
          textAlign="center"
          fontWeight={400}
          mb="8px"
        >
          Check Your Email
        </H3>
        <Text
          color="white"
          textAlign="center"
          textStyle="body"
          mb="32px"
        >
          A verification code has been sent to {obscureEmail(email)}.
        </Text>

        <Box display="flex" justifyContent="center" w="100%" mb="24px">
          <VerificationCodeInput
            length={6}
            value={code}
            onValueChange={setCode}
            handleVerification={handleSubmit}
          />
        </Box>

        {error && (
          <Text color="negative" mb="16px" textStyle="bodySmall">
            {error}
          </Text>
        )}
        <Text
          color="white"
          textAlign="center"
          textStyle="bodySuperSmall"
          mb="16px"
        >
          Didn't receive a code?
        </Text>
        <Box w="100%" maxW="396px">
          <ButtonWithTimer
            timerInterval={RESEND_INTERVAL_SECONDS}
            handleClick={handleResendCode}
          />
        </Box>
      </Box>
    </Box>
  )
}
